/**
 * Sentiment Vote Buttons Component
 * 
 * CoinMarketCap-style sentiment voting (Bullish/Bearish)
 * Shows percentage distribution and allows users to vote
 */

'use client';

import React, { useState, useEffect } from 'react';
import { useCollectionSentiment } from '@/hooks/useCollectionSentiment';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface SentimentVoteButtonsProps {
  collectionSlug: string;
  className?: string;
  layout?: 'horizontal' | 'vertical';
  showPercentages?: boolean;
  showVoteCounts?: boolean;
}

export function SentimentVoteButtons({
  collectionSlug,
  className = '',
  layout = 'horizontal',
  showPercentages = true,
  showVoteCounts = true,
}: SentimentVoteButtonsProps) {
  // Track if component is mounted (client-side only)
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const {
    bullishVotes,
    bearishVotes,
    totalVotes,
    bullishPercentage,
    bearishPercentage,
    userVote,
    isLoading,
    isVoting,
    error,
    canVote,
    cooldownSeconds,
    vote,
  } = useCollectionSentiment(collectionSlug);

  const handleVote = async (sentiment: 'bullish' | 'bearish') => {
    if (!canVote || isVoting) return;
    await vote(sentiment);
  };

  // Format percentage for display
  const formatPercentage = (percentage: number): string => {
    return `${Math.round(percentage)}%`;
  };

  // Format vote count
  const formatCount = (num: number): string => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  // Don't render until mounted to prevent hydration mismatch
  // Show loading skeleton during SSR and initial mount
  if (!isMounted || isLoading) {
    return (
      <div className={`${className}`}>
        {/* Title skeleton */}
        <div className="mb-2">
          <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
            Market Sentiment
          </h3>
        </div>

        {/* Button skeletons - match exact button dimensions */}
        <div
          className={`
          flex gap-2
          ${layout === 'vertical' ? 'flex-col' : 'flex-row'}
        `}
        >
          <div className="flex-1 px-3 py-2 bg-gray-200 dark:bg-brand-dark-lighter rounded-lg animate-pulse">
            <div className="h-4 w-full" />
          </div>
          <div className="flex-1 px-3 py-2 bg-gray-200 dark:bg-brand-dark-lighter rounded-lg animate-pulse">
            <div className="h-4 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Title with vote count */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
          Market Sentiment
        </h3>
        {totalVotes > 0 && (
          <span className="text-[10px] text-gray-500 dark:text-gray-400">
            {formatCount(totalVotes)} {totalVotes === 1 ? 'vote' : 'votes'}
          </span>
        )}
      </div>

      {/* CoinMarketCap-style horizontal percentage bar */}
      {totalVotes > 0 && (
        <div className="mb-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <TrendingUp size={14} />
              <span className="text-xs font-semibold">{formatPercentage(bullishPercentage)}</span>
            </div>
            <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
              <div
                className="bg-emerald-500 transition-all duration-500"
                style={{ width: `${bullishPercentage}%` }}
              />
              <div
                className="bg-rose-500 transition-all duration-500"
                style={{ width: `${bearishPercentage}%` }}
              />
            </div>
            <div className="flex items-center gap-1 text-rose-600 dark:text-rose-400">
              <span className="text-xs font-semibold">{formatPercentage(bearishPercentage)}</span>
              <TrendingDown size={14} />
            </div>
          </div>
        </div>
      )}

      {/* Vote buttons - Compact */}
      <div className="flex gap-2">
        {/* Bullish button */}
        <button
          onClick={() => handleVote('bullish')}
          disabled={!canVote || isVoting}
          className={`
            flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg
            transition-all duration-200 font-medium text-xs
            ${userVote === 'bullish'
              ? 'bg-emerald-500 text-white shadow-md ring-2 ring-emerald-400'
              : canVote
                ? 'bg-white dark:bg-brand-dark-lighter text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                : 'bg-gray-100 dark:bg-brand-dark-lighter text-gray-400 dark:text-gray-600 cursor-not-allowed border border-gray-200 dark:border-neutral-800'
            }
          `}
          title={
            canVote
              ? 'Vote Bullish'
              : cooldownSeconds > 0
                ? `Wait ${Math.ceil(cooldownSeconds / 3600)}h to change vote`
                : 'Please wait'
          }
        >
          <motion.div
            animate={
              isVoting && userVote === 'bullish'
                ? { scale: [1, 1.2, 1] }
                : {}
            }
            transition={{ duration: 0.3 }}
          >
            <TrendingUp size={14} />
          </motion.div>
          <span>Bullish</span>
        </button>

        {/* Bearish button */}
        <button
          onClick={() => handleVote('bearish')}
          disabled={!canVote || isVoting}
          className={`
            flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg
            transition-all duration-200 font-medium text-xs
            ${userVote === 'bearish'
              ? 'bg-rose-500 text-white shadow-md ring-2 ring-rose-400'
              : canVote
                ? 'bg-white dark:bg-brand-dark-lighter text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 border border-rose-200 dark:border-rose-800'
                : 'bg-gray-100 dark:bg-brand-dark-lighter text-gray-400 dark:text-gray-600 cursor-not-allowed border border-gray-200 dark:border-neutral-800'
            }
          `}
          title={
            canVote
              ? 'Vote Bearish'
              : cooldownSeconds > 0
                ? `Wait ${Math.ceil(cooldownSeconds / 3600)}h to change vote`
                : 'Please wait'
          }
        >
          <motion.div
            animate={
              isVoting && userVote === 'bearish'
                ? { scale: [1, 1.2, 1] }
                : {}
            }
            transition={{ duration: 0.3 }}
          >
            <TrendingDown size={14} />
          </motion.div>
          <span>Bearish</span>
        </button>
      </div>

      {/* Cooldown indicator */}
      {cooldownSeconds > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400 text-center"
        >
          You can change your vote in {Math.ceil(cooldownSeconds / 3600)} hours
        </motion.div>
      )}

      {/* Error message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1.5 text-[10px] text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-1.5 rounded"
        >
          {error}
        </motion.div>
      )}

      {/* No votes yet message */}
      {totalVotes === 0 && !userVote && (
        <p className="mt-1.5 text-[10px] text-gray-500 dark:text-gray-400 text-center">
          Be the first to vote!
        </p>
      )}
    </div>
  );
}

